/**
 * Guided capture — the 8-question interview.
 *
 * The mobile-priority screen (design handoff §2). Runs entirely signed-out;
 * answers persist to localStorage after every submission, and the whole draft
 * is claimed server-side when the scan runs.
 *
 * Copy is verbatim from the prototype's QS array via @ideabrief/core. Do not
 * paraphrase question text, hints, or placeholders.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { INTERVIEW_AREAS, INTERVIEW_QUESTIONS } from '@ideabrief/core';
import type { InterviewQuestionId } from '@ideabrief/core';
import { Logo, Mono, Wordmark } from '../components/ui';
import { isComplete, loadDraft, saveDraft, type DraftAnswer } from '../lib/draft';

/** Matches the prototype's pacing: long enough to read, short enough to trust. */
const THINK_MS = 820;
const THINK_BEFORE_SUMMARY_MS = 1100;

export function Capture() {
  const navigate = useNavigate();
  const initial = loadDraft();

  const complete = isComplete(initial);
  // A finished interview's `step` sits one past the end (and a pre-fix draft's
  // may be anywhere), so clamp into range: this screen still renders for the
  // frame before the redirect below lands, and must not index off the array.
  const initialStep = Math.min(
    Math.max(initial.step, 0),
    INTERVIEW_QUESTIONS.length - 1,
  );
  const [step, setStep] = useState(initialStep);
  const [answers, setAnswers] = useState<
    Partial<Record<InterviewQuestionId, DraftAnswer>>
  >(initial.answers);
  // Seed from the saved answer for this step. Coming back to a question you
  // have already answered must show that answer, not an empty box — editAnswer()
  // did this for thread rows, but a fresh mount (reload, browser Back) did not.
  const [draft, setDraft] = useState(() => {
    const a = initial.answers[INTERVIEW_QUESTIONS[initialStep]!.id];
    return a && !a.skipped ? a.answer : '';
  });
  const [thinking, setThinking] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const threadRef = useRef<HTMLDivElement>(null);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  // Clear pending timers on unmount so a navigation mid-"thinking" cannot
  // advance the interview after the screen is gone.
  useEffect(
    () => () => {
      for (const t of timers.current) clearTimeout(t);
    },
    [],
  );

  // A finished interview belongs on the launch screen. Without this, every
  // route back to /capture — browser Back after a failed sign-up, "Keep
  // refining", a bookmark — re-presented the last question as if it were
  // unanswered. `replace` so Back from /launch does not bounce straight here.
  useEffect(() => {
    if (complete) navigate('/launch', { replace: true });
  }, [complete, navigate]);

  useEffect(() => {
    if (!thinking) inputRef.current?.focus();
  }, [step, thinking]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  const question = INTERVIEW_QUESTIONS[step];
  const total = INTERVIEW_QUESTIONS.length;

  /**
   * `step` in storage is the FURTHEST point reached, never the cursor. Editing
   * an earlier answer from the thread moves the cursor back, and writing that
   * lower number would un-complete a finished interview — the user would answer
   * question 4 again and be dropped back into 5, 6, 7, 8 they had already done.
   */
  function persist(
    next: Partial<Record<InterviewQuestionId, DraftAnswer>>,
    nextStep: number,
  ) {
    saveDraft({
      step: Math.max(nextStep, loadDraft().step),
      answers: next,
      overrides: initial.overrides,
    });
  }

  function record(value: string, skipped: boolean) {
    if (!question) return;

    const next = {
      ...answers,
      [question.id]: { answer: skipped ? '' : value.trim(), skipped },
    };
    setAnswers(next);
    setDraft('');

    const last = step === total - 1;
    const message = skipped
      ? 'Noted — moving on…'
      : last
        ? 'Pulling your answers into a summary…'
        : (question.ack ?? 'Reading your answer…');

    setThinking(message);
    // Always step + 1. On the LAST question that is INTERVIEW_QUESTIONS.length —
    // the completion sentinel isComplete() reads. Persisting `step` here instead
    // is what made a finished interview look unstarted on question 8.
    persist(next, step + 1);

    const t = setTimeout(
      () => {
        setThinking(null);
        if (last) {
          navigate('/launch');
          return;
        }
        setStep(step + 1);
        setToast('Draft saved');
        // Keep the newest thread row in view after it collapses.
        const s = setTimeout(() => {
          const el = threadRef.current;
          if (el) el.scrollTop = el.scrollHeight;
        }, 80);
        timers.current.push(s);
      },
      last ? THINK_BEFORE_SUMMARY_MS : THINK_MS,
    );
    timers.current.push(t);
  }

  function editAnswer(index: number) {
    const q = INTERVIEW_QUESTIONS[index];
    if (!q) return;
    setStep(index);
    setDraft(answers[q.id]?.answer ?? '');
    setThinking(null);
  }

  if (!question) return null;

  const answered = INTERVIEW_QUESTIONS.slice(0, step);
  const areaLabel = INTERVIEW_AREAS[question.area]?.short ?? '';

  // "≈6 MIN LEFT" — the prototype shows remaining time, not a question count.
  // The landing promises about six minutes for eight questions, so ~45s each.
  const minutesLeft = Math.max(1, Math.ceil(((total - step) * 45) / 60));

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          background: 'rgba(251,250,248,.86)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid var(--line2)',
        }}
      >
        <div
          style={{
            maxWidth: 860,
            margin: '0 auto',
            padding: '14px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <Logo />
          <Wordmark />
          <div style={{ flex: 1 }} />
          <span
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: 3,
                background: 'var(--green)',
              }}
            />
            <Mono size={10.5}>DRAFT SAVED</Mono>
          </span>
          <button
            onClick={() => navigate('/')}
            style={{
              background: 'none',
              border: '1px solid var(--line)',
              borderRadius: 8,
              padding: '7px 12px',
              fontSize: 13,
              color: 'var(--ink2)',
              cursor: 'pointer',
            }}
          >
            Save &amp; exit
          </button>
        </div>

        {/* Progress: one segment per coverage area, not per question. */}
        <div
          style={{
            maxWidth: 860,
            margin: '0 auto',
            padding: '0 20px 12px',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <div style={{ display: 'flex', gap: 12, flex: 1 }}>
            {INTERVIEW_AREAS.map((_, i) => (
              <div
                key={i}
                style={{
                  height: 4,
                  flex: 1,
                  borderRadius: 3,
                  background:
                    i <= question.area ? 'var(--acc)' : 'var(--line)',
                }}
              />
            ))}
          </div>
          <Mono size={10.5}>
            {areaLabel.toUpperCase()} · ≈{minutesLeft} MIN LEFT
          </Mono>
        </div>
      </header>

      <main
        style={{
          maxWidth: 860,
          margin: '0 auto',
          padding: '26px 20px 40px',
          // The question block sits vertically centred in the space below the
          // header, as in the prototype — not pinned to the top.
          minHeight: 'calc(100vh - 124px)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        {answered.length > 0 && (
          <div
            ref={threadRef}
            style={{
              maxHeight: 186,
              overflowY: 'auto',
              marginBottom: 28,
              maskImage:
                'linear-gradient(to bottom, transparent 0, #000 34px)',
              WebkitMaskImage:
                'linear-gradient(to bottom, transparent 0, #000 34px)',
            }}
          >
            {answered.map((q, i) => {
              const a = answers[q.id];
              return (
                <div
                  key={q.id}
                  style={{
                    display: 'flex',
                    gap: 12,
                    padding: '10px 0',
                    borderBottom: '1px solid var(--line2)',
                    opacity: 0.72,
                  }}
                >
                  <Mono size={10.5}>{String(i + 1).padStart(2, '0')}</Mono>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: 'var(--muted)' }}>
                      {q.q}
                    </div>
                    <div
                      style={{
                        fontSize: 14.5,
                        color: a?.skipped ? 'var(--muted)' : 'var(--ink2)',
                        marginTop: 2,
                      }}
                    >
                      {a?.skipped
                        ? "Skipped — we'll research this"
                        : a?.answer}
                    </div>
                  </div>
                  <button
                    onClick={() => editAnswer(i)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--acc)',
                      fontSize: 13,
                      cursor: 'pointer',
                      alignSelf: 'flex-start',
                    }}
                  >
                    Edit
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {thinking ? (
          <div style={{ padding: '40px 0' }}>
            <Byline step={step} total={total} />
            <div
              style={{ display: 'flex', gap: 5, alignItems: 'center', marginTop: 18 }}
            >
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: 4,
                    background: 'var(--acc)',
                    animation: `ibBounce 1.1s ${i * 0.16}s infinite ease-in-out`,
                  }}
                />
              ))}
              <span
                style={{ marginLeft: 8, fontSize: 15, color: 'var(--muted)' }}
              >
                {thinking}
              </span>
            </div>
          </div>
        ) : (
          <div key={question.id} style={{ animation: 'ibRise .34s cubic-bezier(.2,.7,.3,1)' }}>
            <Byline step={step} total={total} />

            <h1
              style={{
                fontSize: 30,
                fontWeight: 600,
                lineHeight: 1.22,
                letterSpacing: '-.025em',
                margin: '14px 0 0',
                maxWidth: '28ch',
              }}
            >
              {question.q}
            </h1>

            <p
              style={{
                fontSize: 15,
                color: 'var(--muted)',
                lineHeight: 1.55,
                margin: '10px 0 0',
                maxWidth: '52ch',
              }}
            >
              {question.hint}
            </p>

            {question.chips && (
              <div
                style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 16 }}
              >
                {question.chips.map((chip) => (
                  <button
                    key={chip}
                    // Chips SEED the textarea, they do not submit (Journey spec
                    // §06). The stale README says otherwise.
                    onClick={() => {
                      setDraft(chip);
                      inputRef.current?.focus();
                    }}
                    style={{
                      background: 'var(--card)',
                      border: '1px solid var(--line)',
                      borderRadius: 20,
                      padding: '8px 14px',
                      fontSize: 13.5,
                      color: 'var(--ink2)',
                      cursor: 'pointer',
                    }}
                  >
                    {chip}
                  </button>
                ))}
              </div>
            )}

            <div
              style={{
                background: 'var(--card)',
                border: '1px solid var(--line)',
                borderRadius: 14,
                padding: '6px 6px 6px 0',
                marginTop: 18,
                display: 'flex',
                gap: 12,
                alignItems: 'flex-end',
                boxShadow: '0 1px 2px rgba(24,23,32,.05)',
              }}
            >
              <textarea
                ref={inputRef}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  // Enter sends, Shift+Enter is a newline.
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (draft.trim()) record(draft, false);
                  }
                }}
                placeholder={question.ph}
                rows={2}
                style={{
                  flex: 1,
                  border: 'none',
                  outline: 'none',
                  resize: 'none',
                  fontSize: 16,
                  fontFamily: 'inherit',
                  color: 'var(--ink)',
                  background: 'transparent',
                  // Keeps the tap target comfortable on mobile. The card no
                  // longer pads, so the textarea owns its inset.
                  minHeight: 56,
                  lineHeight: 1.5,
                  padding: '14px 8px 14px 16px',
                }}
              />
              <button
                onClick={() => draft.trim() && record(draft, false)}
                style={{
                  background: 'var(--acc)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 10,
                  padding: '13px 20px',
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: 'pointer',
                  opacity: draft.trim() ? 1 : 0.45,
                  transition: 'opacity .18s ease',
                  minHeight: 44,
                }}
              >
                Send
              </button>
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                marginTop: 12,
                flexWrap: 'wrap',
              }}
            >
              {/* ONE escape hatch, per the spec. Offering both "Skip this one"
                  and "I don't know yet" wrote the identical skipped record
                  twice over while implying the two differed. */}
              <button onClick={() => record('', true)} style={linkButton}>
                Not sure — we&apos;ll research it
              </button>
              <div style={{ flex: 1 }} />
              <Mono size={10.5}>↵ TO SEND · ⇧↵ NEW LINE</Mono>
            </div>
          </div>
        )}
      </main>

      {toast && (
        <div
          role="status"
          style={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'var(--ink)',
            color: '#fff',
            borderRadius: 20,
            padding: '10px 18px',
            fontSize: 13.5,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            zIndex: 20,
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: 3,
              background: 'var(--green)',
            }}
          />
          {toast}
        </div>
      )}
    </div>
  );
}

const linkButton = {
  background: 'none',
  border: 'none',
  padding: 0,
  fontSize: 13.5,
  color: 'var(--muted)',
  textDecoration: 'underline',
  cursor: 'pointer',
} as const;

function Byline({ step, total }: { step: number; total: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <Logo size={20} />
      <Mono size={11}>
        IDEABRIEF AI · QUESTION {step + 1} OF {total}
      </Mono>
    </div>
  );
}
